import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
  Filter,
  FilterType,
} from '@syncfusion/ej2-react-grids';
import { useGetCasesQuery, MedNecCaseView } from '../../../services/MedNecService';
import {
  patientTemplate,
  memberIdTemplate,
  cptTemplate,
  dateTemplate,
  docsTemplate,
} from './columnTemplates';

const pageSettings = { pageSize: 25, pageSizes: [10, 25, 50, 100] };
const filterSettings = { type: 'Excel' as FilterType };

const gridContainerStyles = {
  '& .e-grid': {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontFamily: 'inherit',
  },
  '& .e-grid .e-gridheader': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .e-grid .e-headercell': {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '14px',
  },
  '& .e-grid .e-row:hover': {
    backgroundColor: '#f8fafc',
  },
  '& .e-grid .e-altrow': {
    backgroundColor: '#ffffff',
  },
};

const SEARCH_FIELDS: (keyof MedNecCaseView)[] = [
  'payerName',
  'physicianName',
  'status',
  'facilityCode',
];

const MedNecWorklistGrid: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get('status') || '';
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';

  const { data: cases = [], isLoading, error } = useGetCasesQuery(
    { status, facility: center },
    { skip: !status || !center },
  );

  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return cases;
    const lower = searchTerm.toLowerCase();
    return cases.filter((c: MedNecCaseView) => {
      const patientName = c.patient
        ? `${c.patient.firstName} ${c.patient.lastName}`.toLowerCase()
        : '';
      const memberId = c.patient?.memberId?.toLowerCase() || '';
      return (
        patientName.includes(lower) ||
        memberId.includes(lower) ||
        SEARCH_FIELDS.some((f) => String(c[f] ?? '').toLowerCase().includes(lower)) ||
        c.cptCodes?.some((code: string) => code.toLowerCase().includes(lower))
      );
    });
  }, [cases, searchTerm]);

  const handleRowSelected = useCallback(
    (args: any) => {
      const selectedItem: MedNecCaseView | undefined = args.data;
      if (selectedItem?.id) {
        const params = new URLSearchParams();
        params.append('id', selectedItem.id);
        if (company) params.append('company', company);
        if (center) params.append('center', center);
        navigate(`/mednec/cases/edit?${params.toString()}`);
      }
    },
    [company, center, navigate],
  );

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading cases...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Error loading cases. Please try again.</Typography>
      </Box>
    );
  }

  if (cases.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
          No cases found
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          No cases with this status in {center}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by patient, member ID, payer, physician, CPT..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#f8fafc',
            },
          }}
        />
      </Box>

      {searchTerm.trim() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing {filteredItems.length} of {cases.length} cases
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      )}

      {filteredItems.length === 0 && searchTerm.trim() ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No cases match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: &quot;{searchTerm}&quot;
          </Typography>
        </Box>
      ) : (
        <Box sx={gridContainerStyles}>
          <GridComponent
            dataSource={filteredItems}
            allowPaging={true}
            allowSorting={true}
            allowFiltering={true}
            pageSettings={pageSettings}
            filterSettings={filterSettings}
            height="500px"
            rowSelected={handleRowSelected}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="patient"
                headerText="Patient"
                width="170"
                textAlign="Left"
                template={patientTemplate}
              />
              <ColumnDirective
                field="memberId"
                headerText="Member ID"
                width="130"
                textAlign="Left"
                template={memberIdTemplate}
              />
              <ColumnDirective
                field="payerName"
                headerText="Payer"
                width="150"
                textAlign="Left"
              />
              <ColumnDirective
                field="physicianName"
                headerText="Physician"
                width="150"
                textAlign="Left"
              />
              <ColumnDirective
                field="cptCodes"
                headerText="CPT Codes"
                width="140"
                textAlign="Left"
                template={cptTemplate}
              />
              <ColumnDirective
                field="documentCount"
                headerText="Docs"
                width="80"
                textAlign="Center"
                template={docsTemplate}
              />
              <ColumnDirective
                field="createdAt"
                headerText="Created"
                width="120"
                textAlign="Left"
                template={dateTemplate}
              />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Filter]} />
          </GridComponent>
        </Box>
      )}
    </Box>
  );
};

export default MedNecWorklistGrid;
